import { useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

export const useCollaboration = (documentType, documentId, userId, userName, enabled = true) => {
    const [collaborators, setCollaborators] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [myColor, setMyColor] = useState(null);
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const onContentUpdateRef = useRef(null);
    const onCursorUpdateRef = useRef(null);

    // Connect to WebSocket
    const connect = useCallback(() => {
        if (!enabled || !documentId || !userId) return;

        const wsUrl = `${WS_URL}/api/collaboration/ws/${documentType}/${documentId}?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`;
        
        try {
            const ws = new WebSocket(wsUrl);
            
            ws.onopen = () => {
                console.log('Collaboration connected');
                setIsConnected(true);
            };
            
            ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    handleMessage(message);
                } catch (e) {
                    console.error('Failed to parse message:', e);
                }
            };
            
            ws.onclose = () => {
                console.log('Collaboration disconnected');
                setIsConnected(false);
                
                // Attempt reconnection after 3 seconds
                if (enabled) {
                    reconnectTimeoutRef.current = setTimeout(connect, 3000);
                }
            };
            
            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
            };
            
            wsRef.current = ws;
        } catch (e) {
            console.error('Failed to create WebSocket:', e);
        }
    }, [documentType, documentId, userId, userName, enabled]);

    // Handle incoming messages
    const handleMessage = useCallback((message) => {
        const { type, user_id, user_name, data } = message;
        
        switch (type) {
            case 'sync':
                // Initial sync when joining
                setCollaborators(data.collaborators || []);
                setMyColor(data.your_color);
                if (data.content && onContentUpdateRef.current) {
                    onContentUpdateRef.current(data.content, 'sync');
                }
                break;
                
            case 'join':
                // New user joined
                setCollaborators(data.collaborators || []);
                break;
                
            case 'leave':
                // User left
                setCollaborators(data.collaborators || []);
                break;
                
            case 'edit':
                // Content was edited by another user
                if (onContentUpdateRef.current) {
                    onContentUpdateRef.current(data.content, 'edit', {
                        userId: user_id,
                        userName: user_name,
                        position: data.position,
                        operation: data.operation
                    });
                }
                break;
                
            case 'cursor':
                // Another user's cursor moved
                if (onCursorUpdateRef.current) {
                    onCursorUpdateRef.current({
                        userId: user_id,
                        userName: user_name,
                        position: data.position,
                        color: data.user_color
                    });
                }
                // Update collaborator cursor position
                setCollaborators(prev => prev.map(c => 
                    c.user_id === user_id 
                        ? { ...c, cursor_position: data.position }
                        : c
                ));
                break;
                
            case 'selection':
                // Another user selected text
                setCollaborators(prev => prev.map(c => 
                    c.user_id === user_id 
                        ? { ...c, selection_start: data.start, selection_end: data.end }
                        : c
                ));
                break;
                
            default:
                console.log('Unknown message type:', type);
        }
    }, []);

    // Send edit to others
    const sendEdit = useCallback((content, position = null, operation = 'replace') => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'edit',
                content,
                position,
                operation
            }));
        }
    }, []);

    // Send cursor position
    const sendCursor = useCallback((position) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'cursor',
                position
            }));
        }
    }, []);

    // Send text selection
    const sendSelection = useCallback((start, end) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'selection',
                start,
                end
            }));
        }
    }, []);

    // Request full sync
    const requestSync = useCallback(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'sync_request'
            }));
        }
    }, []);

    // Register callbacks
    const onContentUpdate = useCallback((callback) => {
        onContentUpdateRef.current = callback;
    }, []);

    const onCursorUpdate = useCallback((callback) => {
        onCursorUpdateRef.current = callback;
    }, []);

    // Connect on mount
    useEffect(() => {
        connect();
        
        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, [connect]);

    return {
        collaborators: collaborators.filter(c => c.user_id !== userId), // Exclude self
        isConnected,
        myColor,
        sendEdit,
        sendCursor,
        sendSelection,
        requestSync,
        onContentUpdate,
        onCursorUpdate
    };
};

export default useCollaboration;
